/**
 * Adapter K1 de FINANZAS (F3): registros → dataset plano exportable
 * (CSV RFC-4180 / PDF imprimible). El dinero sale como DECIMAL honesto
 * (`amount_minor` -> `"12.34"`), never raw integer (EXPORT_K1 §4).
 */

import {
  buildDataset,
  type ExportColumn,
  type ExportDataset,
  type ExportMetadata,
} from "../export";
import { minorToDecimal } from "./money";
import type { FinanceRecordLike } from "./summary";

/** Enriched row for export (names already resolved by caller). */
export interface FinanceExportRow extends FinanceRecordLike {
  description: string | null;
  fundName?: string | null;
  categoryName?: string | null;
  receipt_url?: string | null;
}

export function financeExportColumns(): ExportColumn<FinanceExportRow>[] {
  return [
    { key: "date", header: "Date", accessor: (r) => (r.occurred_at ?? "").slice(0, 10) },
    { key: "kind", header: "Type", accessor: (r) => r.kind ?? "income" },
    { key: "description", header: "Description", accessor: (r) => r.description ?? "" },
    // Honest decimal: domain amounts are positive (sign provided by
    // Type column), so anti-injection sanitizing does not alter numbers.
    {
      key: "amount",
      header: "Amount",
      accessor: (r) => minorToDecimal(r.amount_minor ?? 0, r.currency),
    },
    { key: "currency", header: "Currency", accessor: (r) => r.currency ?? "" },
    { key: "fund", header: "Fund", accessor: (r) => r.fundName ?? "" },
    { key: "category", header: "Category", accessor: (r) => r.categoryName ?? "" },
    { key: "receipt", header: "Receipt", accessor: (r) => r.receipt_url ?? "" },
  ];
}

export function buildFinanceDataset(
  rows: readonly FinanceExportRow[],
  metadata: ExportMetadata = {},
): ExportDataset {
  return buildDataset(financeExportColumns(), rows, {
    title: metadata.title ?? "Finances",
    ...metadata,
  });
}
